import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MeasurementInput, medicalMeasurementsApi } from '../measurements';

export const measurementKeys = {
  list: (studentId: string) => ['medical', 'measurements', studentId] as const,
  chart: (studentId: string) => ['medical', 'growth-chart', studentId] as const,
};

/** Both caches after a write: the charts are drawn from a second endpoint over the same rows. */
function invalidateMeasurements(qc: ReturnType<typeof useQueryClient>, studentId: string) {
  void qc.invalidateQueries({ queryKey: measurementKeys.list(studentId) });
  void qc.invalidateQueries({ queryKey: measurementKeys.chart(studentId) });
}

export function useMeasurementsQuery(studentId: string) {
  return useQuery({
    queryKey: measurementKeys.list(studentId),
    queryFn: () => medicalMeasurementsApi.list(studentId),
    enabled: Boolean(studentId),
  });
}

export function useGrowthChartQuery(studentId: string) {
  return useQuery({
    queryKey: measurementKeys.chart(studentId),
    queryFn: () => medicalMeasurementsApi.chart(studentId),
    enabled: Boolean(studentId),
  });
}

export function useCreateMeasurementMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MeasurementInput) => medicalMeasurementsApi.create(studentId, data),
    onSuccess: () => invalidateMeasurements(qc, studentId),
  });
}

export function useUpdateMeasurementMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MeasurementInput }) => medicalMeasurementsApi.update(id, data),
    onSuccess: () => invalidateMeasurements(qc, studentId),
  });
}

export function useDeleteMeasurementMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => medicalMeasurementsApi.remove(id),
    onSuccess: () => invalidateMeasurements(qc, studentId),
  });
}
